import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import TypingEngine from "../components/TypingEngine";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PRACTICE_TEXT = {
    english:
        "practice makes progress. type each word with intention and rhythm. the goal is not speed alone but flow and accuracy woven together.",
    nepali:
        "अभ्यासले प्रगति ल्याउँछ। हरेक शब्द ध्यान दिएर टाइप गर्नुहोस्। लक्ष्य केवल गति होइन, तर शुद्धता र लय पनि हो।",
};

export default function Practice() {
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const [lang, setLang] = useState(params.get("lang") || "english");
    const [text, setText] = useState("");
    const [lessonId, setLessonId] = useState(params.get("lesson") || null);
    const [result, setResult] = useState(null);
    const [key, setKey] = useState(0); // remount to reset

    const loadText = useCallback(async () => {
        const lessonQ = params.get("lesson");
        if (lessonQ) {
            const { data } = await api.get(`/lessons/${lessonQ}`);
            setText(data.text);
            setLessonId(data.id);
        } else {
            setText(PRACTICE_TEXT[lang]);
            setLessonId(null);
        }
    }, [params, lang]);

    useEffect(() => {
        loadText();
    }, [loadText]);

    const handleComplete = async (r) => {
        setResult(r);
        if (user) {
            try {
                await api.post("/results", {
                    language: lang,
                    mode: lessonId ? "lesson" : "practice",
                    wpm: r.wpm,
                    accuracy: r.accuracy,
                    duration_seconds: r.durationSeconds,
                    correct_chars: r.correct,
                    incorrect_chars: r.incorrect,
                    total_chars: r.total,
                    lesson_id: lessonId,
                    text_snippet: text.slice(0, 80),
                });
            } catch (e) {
                // ignore
            }
        }
    };

    const switchLang = (l) => {
        setLang(l);
        setLessonId(null);
        setResult(null);
        const p = new URLSearchParams(params);
        p.set("lang", l);
        p.delete("lesson");
        setParams(p);
        setKey((k) => k + 1);
    };

    const newText = async () => {
        setResult(null);
        const { data } = await api.get(`/test-text?language=${lang}`);
        setText(data.text);
        setLessonId(null);
        const p = new URLSearchParams(params);
        p.delete("lesson");
        setParams(p);
        setKey((k) => k + 1);
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <p className="eyebrow">Free Practice</p>
                    <h1 className="font-heading text-4xl font-semibold tracking-tight mt-2">Practice Room</h1>
                </div>
                <div className="flex items-center gap-3">
                    <div className="inline-flex rounded-full border border-[var(--lt-border)] p-1 bg-[var(--lt-bg-2)]/60">
                        {["english", "nepali"].map((l) => (
                            <button
                                key={l}
                                onClick={() => switchLang(l)}
                                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                                    lang === l
                                        ? "bg-[var(--lt-terracotta)] text-white"
                                        : "text-[var(--lt-muted)]"
                                }`}
                                data-testid={`practice-lang-${l}`}
                            >
                                {l === "english" ? "English" : "नेपाली"}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={newText}
                        className="btn-ghost-line text-sm"
                        data-testid="practice-new-text-btn"
                    >
                        New text
                    </button>
                </div>
            </div>

            {text && (
                <TypingEngine
                    key={key}
                    text={text}
                    language={lang}
                    mode="practice"
                    onComplete={handleComplete}
                />
            )}

            {result?.finalized && (
                <div
                    className="glass rounded-2xl p-6 border border-[var(--lt-terracotta)]"
                    data-testid="practice-result-panel"
                >
                    <p className="eyebrow text-[var(--lt-terracotta)]">Run Complete</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
                        <ResultStat label="WPM" value={result.wpm.toFixed(1)} />
                        <ResultStat label="Accuracy" value={`${result.accuracy.toFixed(1)}%`} />
                        <ResultStat label="Time" value={`${result.durationSeconds}s`} />
                        <ResultStat label="Correct" value={result.correct} />
                    </div>
                </div>
            )}
        </div>
    );
}

function ResultStat({ label, value }) {
    return (
        <div>
            <div className="eyebrow">{label}</div>
            <div className="font-mono-stat text-2xl mt-1">{value}</div>
        </div>
    );
}
